import { Table, Text, Box } from '@mantine/core';

import { convertPaymentsToTable } from '../utils/helpers/converter';
import { paymentCallbackStatusTitles, remoteCallbackStatusTitles } from '../utils/const/common';
import { CustomTable, CustomTableMobile, CustomCopyBtn, useResponsive, formatAmount } from '@/shared';
import { dayjsCustom } from '@/shared/utils/helpers/datetime-format';
import { partnerCommissionCalculationBaseTitles } from '@/modules/shops';
import { PaymentStatus } from './payment-status';

import type { TPayment } from '../store/types';

const getAmount = (item: TPayment, withCurrency = false) => (
  <Box>
    <Text inherit ta={'right'}>{formatAmount(item.amount)} {withCurrency ? item.currency : ''}</Text>
    {item.uniqueAmount && (
      <Text size="xs" c="dimmed" ta={'right'}>
        К переводу: {formatAmount(item.uniqueAmount)} {item.uniqueCurrency}
      </Text>
    )}
  </Box>
);

const getCredentials = (item: TPayment) => {
  const credentialsList = item.credentials?.split('/');

  return (
    <Box>
      {credentialsList?.map((credential) => (
        <Text inherit key={credential} ta={'right'} style={{ wordBreak: 'break-all' }}>{credential}</Text>
      ))}
    </Box>
  );
};

const getRemoteCallback = (item: TPayment) => {
  if (item.type === 'custom') return '-';

  if (!item.remoteCallbackReceivedAt) return 'Не получен';

  const status = item.remoteCallbackStatus
    ? remoteCallbackStatusTitles[item.remoteCallbackStatus] ?? item.remoteCallbackStatus
    : 'Получен';

  return (
    <Box>
      <Text inherit ta={'right'}>{status}</Text>
      {item.remoteCallbackAmount && item.remoteCallbackCurrency && (
        <Text inherit ta={'right'}>
          {formatAmount(item.remoteCallbackAmount)} {item.remoteCallbackCurrency}
        </Text>
      )}
      <Text inherit ta={'right'}>
        {dayjsCustom(item.remoteCallbackReceivedAt).format('DD.MM.YYYY / HH:mm')}
      </Text>
    </Box>
  );
};

const getPartnerCommission = (item: TPayment) => {
  if (item.partnerShopId === null || item.partnerCommissionPercent === null) return '-';

  const status = item.partnerCommissionCredited ? 'Зачислена' : 'Не зачислена';
  const calculationBase = item.partnerCommissionCalculationBase
    ? partnerCommissionCalculationBaseTitles[item.partnerCommissionCalculationBase]
    : '-';

  return (
    <Box>
      <Text inherit ta={'right'}>
        {formatAmount(item.partnerCommission)} {item.settlementCurrency}
      </Text>
      <Text inherit ta={'right'}>{item.partnerCommissionPercent}% / магазин {item.partnerShopId}</Text>
      <Text inherit ta={'right'}>База: {calculationBase}</Text>
      <Text inherit ta={'right'}>{status}</Text>
    </Box>
  );
};

type Props = {
  items: TPayment[];
  isLoading?: boolean;
  itemsCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
};

export const PaymentsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();

  const titles = [
    'ID платежа',
    'ID платежа в системе',
    'Создан',
    'Реквизиты',
    // 'Обновлён',
    'Сумма',
    'Оплаченная сумма',
    'Комиссия',
    'Партнёрская комиссия',
    'Валюта',
    'ID клиента',
    'ID клиентской транзакции',
    'ID магазина',
    'Метод',
    'Колбэк платёжной системы',
    'Колбэк магазину',
    'Статус',
  ];

  const rows = convertPaymentsToTable(items).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td miw={100}>
        {item.status !== 'approved' ? <CustomCopyBtn text={item.id.toString()} tooltipPosition="top" rightIcon /> : item.id}
      </Table.Td>
      <Table.Td>
        {item.remoteId ? <CustomCopyBtn text={item.remoteId} tooltipPosition="top" rightIcon /> : '-'}
      </Table.Td>
      <Table.Td>{item.created}</Table.Td>
      <Table.Td>{getCredentials(item)}</Table.Td>
      {/* <Table.Td>{item.updated}</Table.Td> */}
      <Table.Td>{getAmount(item)}</Table.Td>
      <Table.Td>{formatAmount(item.paidAmount)}</Table.Td>
      <Table.Td>{formatAmount(item.commission)} {item.settlementCurrency}</Table.Td>
      <Table.Td>{getPartnerCommission(item)}</Table.Td>
      <Table.Td>{item.currency}</Table.Td>
      <Table.Td>{item.payerId}</Table.Td>
      <Table.Td>{item.clientTransactionId}</Table.Td>
      <Table.Td>{item.shopId}</Table.Td>
      <Table.Td>{`(${item.methodId}) ${item.methodName} [${item.paymentSystemName}]`}</Table.Td>
      <Table.Td>{getRemoteCallback(item)}</Table.Td>
      <Table.Td>{paymentCallbackStatusTitles[item.callbackStatus]}</Table.Td>
      <Table.Td><PaymentStatus payment={item} /></Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = convertPaymentsToTable(items).map((item) => ({
    key: item.id,
    // headerActions: getActions(item),
    items: [
      { name: 'ID платежа', value: item.status !== 'approved' ? <CustomCopyBtn text={item.id.toString()} tooltipPosition="top" leftIcon /> : item.id },
      { name: 'Создан', value: item.created },
      { name: 'Сумма', value: getAmount(item, true) },
      { name: 'Статус', value: <PaymentStatus payment={item} /> },
      { name: 'ID платежа в системе', value: item.remoteId ? <CustomCopyBtn text={item.remoteId} tooltipPosition="top" leftIcon /> : '-' },
      { name: 'Реквизиты', value: getCredentials(item) },
      { name: 'Оплаченная сумма', value: formatAmount(item.paidAmount) },
      {
        name: 'Комиссия',
        value: `${formatAmount(item.commission)} ${item.settlementCurrency}`,
      },
      { name: 'Партнёрская комиссия', value: getPartnerCommission(item) },
      { name: 'Валюта', value: item.currency },
      { name: 'ID клиента', value: item.payerId },
      { name: 'ID клиентской транзакции', value: item.clientTransactionId },
      { name: 'ID магазина', value: item.shopId },
      { name: 'Колбэк платёжной системы', value: getRemoteCallback(item) },
      { name: 'Колбэк магазину', value: paymentCallbackStatusTitles[item.callbackStatus] },
      { name: 'Метод', value: <Text span inherit ta={'right'}>{`(${item.methodId}) ${item.methodName} [${item.paymentSystemName}]`}</Text> },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        titles={titles}
        rows={rows}
        {...props}
      />
    )
    : (
      <CustomTableMobile
        rows={rowsMobile}
        headerPointsCount={4}
        {...props}
      />
    );
};
